import React from "react";
import { motion } from "framer-motion";
import { ChevronRight, Music, Waves } from "lucide-react";

/**
 * ConfigureSession — shared Step 3 sub-block for Private Suite.
 *
 * Two controls available across all four modes
 * (meditation / performance / deep_recovery / health_recovery):
 *   1. Music Picker  — opens the MusicSelection overlay.
 *   2. Binaural Beats toggle — opt-in entrainment layer.
 *
 * Whisper volume is handled by the Subliminal Mode toggle that lives in
 * the parent (Step 3 page) — a precise two-position toggle avoids the
 * iOS Safari `volume` API quirk where slider positions were inaudible.
 */
const ConfigureSession = ({
  premiumMusicTrack,
  onOpenMusicPicker,
  binauralEnabled,
  setBinauralEnabled,
  binauralBeat,
  onOpenBinauralPicker,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="mb-6 space-y-3"
      data-testid="configure-session-block"
    >
      {/* — Choose Music — opens premium 15×2 picker */}
      <button
        type="button"
        onClick={onOpenMusicPicker}
        className="w-full p-4 rounded-2xl text-left transition-all"
        style={{
          background:
            "linear-gradient(180deg, rgba(241,210,156,0.06) 0%, rgba(10,10,8,0.6) 100%)",
          border: "1px solid rgba(241,210,156,0.22)",
          boxShadow:
            "0 0 24px rgba(241,210,156,0.04), inset 0 1px 0 rgba(241,210,156,0.04)",
        }}
        data-testid="music-picker-open"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: "rgba(241,210,156,0.10)" }}
          >
            <Music size={16} className="text-amber-200" />
          </div>
          <div className="flex-1">
            <p className="text-white/85 text-sm">
              {premiumMusicTrack ? premiumMusicTrack.displayName : "Choose music"}
            </p>
            <p className="text-white/40 text-[11px] italic mt-0.5">
              {premiumMusicTrack
                ? "Tap to change · sample any other"
                : "Sample 15 styles · 2 takes each"}
            </p>
          </div>
          <ChevronRight size={16} className="text-white/40" />
        </div>
      </button>

      {/* — Binaural Beats layer toggle — */}
      <div
        className="w-full p-4 rounded-2xl transition-all"
        style={{
          background: binauralEnabled
            ? "linear-gradient(180deg, rgba(241,210,156,0.08) 0%, rgba(10,10,8,0.6) 100%)"
            : "rgba(255,255,255,0.03)",
          border: binauralEnabled
            ? "1px solid rgba(241,210,156,0.30)"
            : "1px solid rgba(255,255,255,0.07)",
        }}
        data-testid="binaural-toggle-card"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: binauralEnabled
                ? "rgba(241,210,156,0.12)"
                : "rgba(255,255,255,0.05)",
            }}
          >
            <Waves
              size={16}
              className={binauralEnabled ? "text-amber-200" : "text-white/55"}
            />
          </div>
          <div className="flex-1">
            <p className="text-white/85 text-sm">Layer Binaural Beats</p>
            <p className="text-white/40 text-[11px] italic mt-0.5">
              {binauralEnabled
                ? binauralBeat
                  ? `${binauralBeat.label} · subtly under the music`
                  : "Tap to choose a band"
                : "Optional brainwave entrainment under the music"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setBinauralEnabled((on) => {
                const next = !on;
                if (next && !binauralBeat) onOpenBinauralPicker();
                return next;
              });
            }}
            className={`w-11 h-6 rounded-full relative transition-colors ${
              binauralEnabled ? "bg-amber-300" : "bg-white/15"
            }`}
            data-testid="binaural-toggle"
            aria-pressed={binauralEnabled}
            aria-label="Toggle binaural beats"
          >
            <div
              className={`absolute top-0.5 w-5 h-5 rounded-full bg-[#1f3a5c] transition-all ${
                binauralEnabled ? "left-[22px]" : "left-0.5"
              }`}
            />
          </button>
        </div>
        {binauralEnabled && (
          <button
            type="button"
            onClick={onOpenBinauralPicker}
            className="mt-3 w-full text-[11px] text-amber-200/85 hover:text-amber-200 tracking-[0.25em] uppercase text-center py-2 rounded-lg border border-amber-300/15"
            data-testid="binaural-choose-band"
          >
            {binauralBeat ? `Change band · ${binauralBeat.label}` : "Choose a band"}
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default ConfigureSession;
